/**
 * Boîtiers GPS de la PLATEFORME (migration 0050) — un seul Traccar partagé, l'IMEI désigne le propriétaire.
 *
 * La supervision attribue chaque boîtier à une organisation cliente (tous les paliers depuis la 0054, Starter compris) ; le client
 * ne peut rien enregistrer lui-même. Logique pure + enveloppes typées des RPC `platform_*boitier*` : les appels prennent la
 * fonction `rpc` en argument (jamais le client importé ici) pour que les tests vérifient la forme exacte des arguments.
 */
import { lireCorpsErreur, type AppelFonction, type AppelRpc } from './accesApi'

export type StatutBoitier = 'stock' | 'attribue' | 'desactive'

/** Une ligne de `platform_lister_boitiers()`. */
export interface LigneBoitier {
  imei: string
  statut: StatutBoitier
  org_id: string | null
  org_nom: string | null
  /** Palier EFFECTIF du propriétaire (règle de la base), null pour un boîtier en stock. */
  palier: string | null
  /** Le propriétaire a-t-il toujours l'option (Pro/Business) ? null en stock. false = rétrogradé : ses positions sont refusées. */
  add_on_ok: boolean | null
  attribue_le: string | null
  attribue_par_nom: string | null
  notes: string | null
  /** Nom donné par le client à la fiche de son boîtier. */
  nom_boitier: string | null
  fiche_active: boolean | null
  dernier_fix: string | null
  derniere_reception: string | null
  signaux_non_attribues: number
  dernier_signal_non_attribue: string | null
}

/** Une ligne de `platform_boitiers_inconnus()` : un IMEI absent du registre qui parle quand même. */
export interface BoitierInconnu {
  imei: string
  nb_messages: number
  premier_signal: string | null
  dernier_signal: string | null
}

/** Une ligne de `platform_orgs_pour_boitiers()` : le menu d'attribution. */
export interface OrgPourBoitier {
  org_id: string
  nom: string
  is_operator: boolean
  palier: string
  eligible: boolean
  nb_boitiers: number
}

export type ActionBoitier = 'ajout_stock' | 'attribution' | 'retrait' | 'activation' | 'desactivation'

/** Une ligne de `platform_journal_boitiers()`. */
export interface EntreeJournalBoitier {
  id: string
  created_at: string
  imei: string
  action: ActionBoitier
  org_avant: string | null
  org_avant_nom: string | null
  org_apres: string | null
  org_apres_nom: string | null
  note: string
  auteur: string | null
}

/* ───────────── Lecture des lignes ───────────── */

const txt = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)
const entier = (v: unknown): number => {
  const n = Number(v ?? 0)
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0
}
const boolOuNull = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null)
const lignes = (d: unknown): Record<string, unknown>[] => (Array.isArray(d) ? (d as Record<string, unknown>[]) : [])

const STATUTS = new Set<string>(['stock', 'attribue', 'desactive'])
const ACTIONS = new Set<string>(['ajout_stock', 'attribution', 'retrait', 'activation', 'desactivation'])

export function ligneBoitier(r: Record<string, unknown>): LigneBoitier {
  return {
    imei: String(r['imei'] ?? ''),
    statut: typeof r['statut'] === 'string' && STATUTS.has(r['statut']) ? (r['statut'] as StatutBoitier) : 'stock',
    org_id: txt(r['org_id']),
    org_nom: txt(r['org_nom']),
    palier: txt(r['palier']),
    add_on_ok: boolOuNull(r['add_on_ok']),
    attribue_le: txt(r['attribue_le']),
    attribue_par_nom: txt(r['attribue_par_nom']),
    notes: txt(r['notes']),
    nom_boitier: txt(r['nom_boitier']),
    fiche_active: boolOuNull(r['fiche_active']),
    dernier_fix: txt(r['dernier_fix']),
    derniere_reception: txt(r['derniere_reception']),
    signaux_non_attribues: entier(r['signaux_non_attribues']),
    dernier_signal_non_attribue: txt(r['dernier_signal_non_attribue']),
  }
}

export function boitierInconnu(r: Record<string, unknown>): BoitierInconnu {
  return {
    imei: String(r['imei'] ?? ''),
    nb_messages: entier(r['nb_messages']),
    premier_signal: txt(r['premier_signal']),
    dernier_signal: txt(r['dernier_signal']),
  }
}

export function orgPourBoitier(r: Record<string, unknown>): OrgPourBoitier {
  return {
    org_id: String(r['org_id']),
    nom: String(r['nom'] ?? ''),
    is_operator: r['is_operator'] === true,
    palier: String(r['palier'] ?? 'starter'),
    // Rien d'éligible sans « true » explicite : la valeur par défaut est la plus prudente.
    eligible: r['eligible'] === true,
    nb_boitiers: entier(r['nb_boitiers']),
  }
}

export function entreeJournalBoitier(r: Record<string, unknown>): EntreeJournalBoitier {
  return {
    id: String(r['id']),
    created_at: String(r['created_at'] ?? ''),
    imei: String(r['imei'] ?? ''),
    action: typeof r['action'] === 'string' && ACTIONS.has(r['action']) ? (r['action'] as ActionBoitier) : 'attribution',
    org_avant: txt(r['org_avant']),
    org_avant_nom: txt(r['org_avant_nom']),
    org_apres: txt(r['org_apres']),
    org_apres_nom: txt(r['org_apres_nom']),
    note: String(r['note'] ?? ''),
    auteur: txt(r['auteur']),
  }
}

/* ───────────── Libellés ───────────── */

export const LIBELLE_STATUT: Record<StatutBoitier, string> = { stock: 'En stock', attribue: 'Attribué', desactive: 'Désactivé' }

export function classeStatut(s: StatutBoitier): 'vert' | 'gris' | 'rouge' {
  return s === 'attribue' ? 'vert' : s === 'desactive' ? 'rouge' : 'gris'
}

export const LIBELLE_PALIER_BOITIER: Record<string, string> = { starter: 'Starter', pro: 'Pro', business: 'Business' }
const palierLisible = (p: string | null): string => (p ? (LIBELLE_PALIER_BOITIER[p] ?? p) : '—')

export const LIBELLE_ACTION_BOITIER: Record<ActionBoitier, string> = {
  ajout_stock: 'Ajouté au stock',
  attribution: 'Attribué',
  retrait: 'Retiré',
  activation: 'Réactivé',
  desactivation: 'Désactivé',
}

/** « Bolt Logistics → Faso Fret », « Stock → Bolt Logistics », « Bolt Logistics → stock ». */
export function decrireJournalBoitier(e: Pick<EntreeJournalBoitier, 'action' | 'org_avant_nom' | 'org_apres_nom'>): string {
  const base = LIBELLE_ACTION_BOITIER[e.action]
  if (e.action === 'attribution') return `${base} à ${e.org_apres_nom ?? 'une organisation supprimée'}`
  if (e.action === 'retrait') return `${base} de ${e.org_avant_nom ?? 'une organisation supprimée'} (retour en stock)`
  return base
}

export function libelleAuteurBoitier(e: Pick<EntreeJournalBoitier, 'auteur'>): string {
  return e.auteur?.trim() || 'Auteur inconnu'
}

/** Ce que dit le menu d'attribution d'une organisation : palier et statut de l'option. */
export function libelleEligibilite(o: Pick<OrgPourBoitier, 'is_operator' | 'palier' | 'eligible'>): string {
  if (o.is_operator) return 'Opératrice — exemptée'
  const p = palierLisible(o.palier)
  return o.eligible ? `${p} — option disponible` : `${p} — option indisponible`
}

/** L'instant le plus récent où on a entendu le boîtier (position d'un attribué, ou signal d'un non attribué). */
export function dernierSignalDe(l: Pick<LigneBoitier, 'derniere_reception' | 'dernier_signal_non_attribue'>): string | null {
  const a = l.derniere_reception ? Date.parse(l.derniere_reception) : NaN
  const b = l.dernier_signal_non_attribue ? Date.parse(l.dernier_signal_non_attribue) : NaN
  if (Number.isNaN(a) && Number.isNaN(b)) return null
  if (Number.isNaN(b) || a >= b) return l.derniere_reception
  return l.dernier_signal_non_attribue
}

/** Ancienneté en millisecondes (≥ 0), null si l'instant est absent ou illisible. */
export function ageSignalMs(iso: string | null, maintenant: number): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  return Number.isNaN(t) ? null : Math.max(0, maintenant - t)
}

/** « à l'instant », « il y a 5 min », « il y a 3 h », « il y a 2 j », « jamais ». */
export function libelleAge(ms: number | null): string {
  if (ms === null) return 'jamais'
  const min = Math.floor(ms / 60_000)
  if (min < 1) return "à l'instant"
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 48) return `il y a ${h} h`
  return `il y a ${Math.floor(h / 24)} j`
}

/** Pastille de fraîcheur : < 10 min en direct (vert), < 24 h ancien (ambre), au-delà silencieux (gris). */
export function classeFraicheur(ms: number | null): 'vert' | 'alerte' | 'gris' {
  if (ms === null) return 'gris'
  return ms <= 10 * 60_000 ? 'vert' : ms <= 24 * 3_600_000 ? 'alerte' : 'gris'
}

/* ───────────── Filtres ───────────── */

export type FiltreBoitiers = 'tous' | StatutBoitier

/** Recherche (IMEI, client, nom du boîtier, note) + filtre de statut. Insensible à la casse et aux accents. */
export function filtrerBoitiers(liste: LigneBoitier[], filtre: FiltreBoitiers, recherche: string): LigneBoitier[] {
  const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const q = norm(recherche.trim())
  return liste.filter((l) => {
    if (filtre !== 'tous' && l.statut !== filtre) return false
    if (!q) return true
    return [l.imei, l.org_nom ?? '', l.nom_boitier ?? '', l.notes ?? ''].some((c) => norm(c).includes(q))
  })
}

/** Nombre de boîtiers par statut, pour les pastilles du filtre. */
export function compterParStatut(liste: LigneBoitier[]): Record<FiltreBoitiers, number> {
  const c: Record<FiltreBoitiers, number> = { tous: liste.length, stock: 0, attribue: 0, desactive: 0 }
  for (const l of liste) c[l.statut]++
  return c
}

/* ───────────── Saisie ───────────── */

export const FORMAT_IMEI = /^[A-Za-z0-9._:-]{1,64}$/
export const NOTE_BOITIER_MAX = 500

/** L'IMEI saisi, rogné ; même règle que la base (`gps_imei_normalise`). */
export function validerImei(saisie: string): { ok: true; imei: string } | { ok: false; message: string } {
  const imei = saisie.trim()
  if (!imei) return { ok: false, message: "Indiquez l'identifiant du boîtier (IMEI)." }
  if (!FORMAT_IMEI.test(imei)) {
    return { ok: false, message: "IMEI invalide : 1 à 64 caractères — lettres, chiffres, point, tiret, deux-points ou souligné, sans espace." }
  }
  return { ok: true, imei }
}

/** Note facultative, rognée ; 500 caractères au plus. */
export function validerNoteBoitier(note: string): { ok: true; note: string | null } | { ok: false; message: string } {
  const n = note.trim()
  if (n.length > NOTE_BOITIER_MAX) return { ok: false, message: `La note fait ${NOTE_BOITIER_MAX} caractères au plus (${n.length} saisis).` }
  return { ok: true, note: n || null }
}

/* ───────────── Erreurs en phrases ───────────── */

const PHRASES: [string, string][] = [
  ['not_platform_admin', "Votre compte n'est pas administrateur de la plateforme : le changement est refusé."],
  ['imei_invalide', "IMEI invalide : 1 à 64 caractères — lettres, chiffres, point, tiret, deux-points ou souligné, sans espace."],
  ['organisation_inconnue', "Cette organisation n'existe plus. Rechargez la page."],
  ['deja_enregistre', 'Ce boîtier figure déjà dans le registre.'],
  ['deja_attribue', "Ce boîtier est déjà attribué à une autre organisation : retirez-le d'abord, puis attribuez-le."],
  ['boitier_desactive', "Ce boîtier est désactivé : réactivez-le avant de l'attribuer."],
  ['pas_attribue', "Ce boîtier n'est attribué à aucune organisation."],
  ['boitier_inconnu', "Ce boîtier n'est pas dans le registre. Rechargez la page."],
  ['parametres_requis', 'Paramètres manquants : rechargez la page puis recommencez.'],
]

/** Traduit l'erreur d'une RPC de boîtier en phrase ; `plan_required` reprend le détail du serveur (« … est au palier Starter … »). */
export function messageBoitier(erreur: unknown): string {
  let message = ''
  let code = ''
  let detail = ''
  if (typeof erreur === 'string') message = erreur
  else if (erreur && typeof erreur === 'object') {
    const o = erreur as { message?: unknown; code?: unknown; details?: unknown }
    message = typeof o.message === 'string' ? o.message : ''
    code = typeof o.code === 'string' ? o.code : ''
    detail = typeof o.details === 'string' ? o.details : ''
  }
  const m = message.toLowerCase()
  if (m.includes('plan_required')) {
    return detail || "Cette organisation ne peut pas recevoir de boîtier GPS."
  }
  for (const [c, phrase] of PHRASES) if (m.includes(c)) return phrase
  if (code === '42501') return "Votre compte n'est pas administrateur de la plateforme : le changement est refusé."
  if (code === 'PGRST202' || m.includes('could not find the function')) {
    return 'La gestion des boîtiers GPS n’est pas encore installée sur la base (migration 0050 à pousser).'
  }
  if (code === 'PGRST301' || m.includes('jwt')) return 'Session expirée — reconnectez-vous puis recommencez.'
  if (m.includes('failed to fetch') || m.includes('load failed') || m.includes('networkerror')) {
    return 'Connexion impossible : vérifiez votre réseau puis réessayez. Rien n’a été enregistré.'
  }
  return `L'opération a échoué${message ? ` (${message})` : ''}. Rien n’a été confirmé.`
}

/* ───────────── Enveloppes des RPC ───────────── */

/** Lève l'erreur de la RPC en conservant son `code`, son `message` (le code métier) et son `details`. */
function leverSiErreur(r: { error: { message: string; code?: string; details?: string } | null }): void {
  if (r.error) throw Object.assign(new Error(r.error.message), { code: r.error.code, details: r.error.details })
}

export async function lireBoitiers(rpc: AppelRpc): Promise<LigneBoitier[]> {
  const r = await rpc('platform_lister_boitiers')
  leverSiErreur(r)
  return lignes(r.data).map(ligneBoitier)
}

export async function lireBoitiersInconnus(rpc: AppelRpc): Promise<BoitierInconnu[]> {
  const r = await rpc('platform_boitiers_inconnus')
  leverSiErreur(r)
  return lignes(r.data).map(boitierInconnu)
}

export async function lireOrgsPourBoitiers(rpc: AppelRpc): Promise<OrgPourBoitier[]> {
  const r = await rpc('platform_orgs_pour_boitiers')
  leverSiErreur(r)
  return lignes(r.data).map(orgPourBoitier)
}

/** Journal d'un boîtier (`imei`) ou de tous (`null`), le plus récent d'abord. */
export async function lireJournalBoitiers(rpc: AppelRpc, imei: string | null = null, limite = 100): Promise<EntreeJournalBoitier[]> {
  const r = await rpc('platform_journal_boitiers', { p_imei: imei, p_limite: limite })
  leverSiErreur(r)
  return lignes(r.data).map(entreeJournalBoitier)
}

/** « Ajouter au stock » (un inconnu devient connu, sans propriétaire). */
export async function ajouterBoitierAuStock(rpc: AppelRpc, imei: string, note: string | null = null): Promise<void> {
  const r = await rpc('platform_ajouter_boitier_stock', { p_imei: imei.trim(), p_note: note?.trim() || null })
  leverSiErreur(r)
}

/** « Attribuer à un client » : le serveur refuse un palier sans l'option (`plan_required`) ; on ne le contrôle pas ici. */
export async function attribuerBoitier(rpc: AppelRpc, imei: string, org: string, note: string | null = null): Promise<void> {
  const r = await rpc('platform_attribuer_boitier', { p_imei: imei.trim(), p_org: org, p_note: note?.trim() || null })
  leverSiErreur(r)
}

export async function retirerBoitier(rpc: AppelRpc, imei: string, note: string | null = null): Promise<void> {
  const r = await rpc('platform_retirer_boitier', { p_imei: imei.trim(), p_note: note?.trim() || null })
  leverSiErreur(r)
}

export async function definirBoitierActif(rpc: AppelRpc, imei: string, actif: boolean, note: string | null = null): Promise<void> {
  const r = await rpc('platform_definir_boitier_actif', { p_imei: imei.trim(), p_actif: actif, p_note: note?.trim() || null })
  leverSiErreur(r)
}

/* ───────────── Alertes GPS des clients (migration 0052) : AGRÉGATS seulement ───────────── */
/*
 * La plateforme ne lit JAMAIS le contenu des alertes ni les zones d'un client : seulement ces compteurs par organisation et la
 * trace du passage planifié (chaque minute). Les deux RPC contrôlent `is_platform_admin()` dans la fonction.
 */

/** Une ligne de `platform_gps_alertes_resume()`. */
export interface ResumeAlertesOrg {
  org_id: string
  org_nom: string
  palier: string
  regles_actives: number
  zones_actives: number
  alertes_ouvertes: number
  alertes_24h: number
  commandes_7j: number
  /** Boîtiers de l'organisation dont la dernière évaluation a échoué (`gps_boitier_etat.derniere_erreur`). */
  erreurs_evaluation: number
}

/** `platform_gps_alertes_sante()` : `dernier_passage` null = le minuteur n'a encore jamais tourné. */
export interface SanteAlertes {
  dernier_passage: string | null
  rapport: Record<string, unknown> | null
}

export function resumeAlertesOrg(r: Record<string, unknown>): ResumeAlertesOrg {
  return {
    org_id: String(r['org_id'] ?? ''),
    org_nom: String(r['org_nom'] ?? ''),
    palier: String(r['palier'] ?? 'starter'),
    regles_actives: entier(r['regles_actives']),
    zones_actives: entier(r['zones_actives']),
    alertes_ouvertes: entier(r['alertes_ouvertes']),
    alertes_24h: entier(r['alertes_24h']),
    commandes_7j: entier(r['commandes_7j']),
    erreurs_evaluation: entier(r['erreurs_evaluation']),
  }
}

export function santeAlertes(r: Record<string, unknown> | undefined): SanteAlertes {
  const rapport = r?.['rapport']
  return {
    dernier_passage: txt(r?.['dernier_passage']),
    rapport: rapport && typeof rapport === 'object' && !Array.isArray(rapport) ? (rapport as Record<string, unknown>) : null,
  }
}

/** La fonction n'existe pas sur la base : la migration 0052 n'est pas encore poussée. */
export function migrationAlertesAbsente(erreur: { message: string; code?: string }): boolean {
  return erreur.code === 'PGRST202' || erreur.message.toLowerCase().includes('could not find the function')
}

/**
 * Compteurs par organisation. `null` = migration 0052 absente (l'écran grise la section) ; liste vide = rien à compter.
 * Toute autre erreur (session, droits, réseau) est levée.
 */
export async function resumeAlertesGps(rpc: AppelRpc): Promise<ResumeAlertesOrg[] | null> {
  const r = await rpc('platform_gps_alertes_resume')
  if (r.error && migrationAlertesAbsente(r.error)) return null
  leverSiErreur(r)
  return lignes(r.data).map(resumeAlertesOrg)
}

/**
 * Trace du dernier passage planifié. `null` = migration 0052 absente ; `{ dernier_passage: null }` = migration posée mais
 * le minuteur n'a jamais tourné (la table de santé est vide).
 */
export async function santeAlertesGps(rpc: AppelRpc): Promise<SanteAlertes | null> {
  const r = await rpc('platform_gps_alertes_sante')
  if (r.error && migrationAlertesAbsente(r.error)) return null
  leverSiErreur(r)
  return santeAlertes(lignes(r.data)[0])
}

/** Le minuteur tourne chaque minute : au-delà de 3 minutes sans passage, il est arrêté ou en panne. */
export const SEUIL_PASSAGE_MS = 3 * 60_000

/** Libellé et verdict du passage planifié ; `maintenant` en millisecondes (Date.now()). */
export function etatPassagePlanifie(dernierPassage: string | null, maintenant: number): { libelle: string; ok: boolean } {
  const ms = ageSignalMs(dernierPassage, maintenant)
  if (ms === null) return { libelle: 'jamais exécuté', ok: false }
  if (ms < SEUIL_PASSAGE_MS) return { libelle: libelleAge(ms), ok: true }
  // Le passage est attendu chaque minute : le retard est l'âge moins cette minute d'attente normale.
  const retardMin = Math.floor(ms / 60_000) - 1
  if (retardMin < 60) return { libelle: `en retard de ${retardMin} min`, ok: false }
  const h = Math.floor(retardMin / 60)
  return { libelle: h < 48 ? `en retard de ${h} h` : `en retard de ${Math.floor(h / 24)} j`, ok: false }
}

/* ───────────── Synchronisation avec Traccar (edge function `gps-commande`, ex-`traccar-commande`) ───────────── */

export type ResultatSynchroTraccar = 'cree' | 'renomme' | 'inchange' | 'simule' | 'non_configure' | 'echec'

/** Réponse de `{ action: 'synchroniser_plateforme', imei }`. `message` : la phrase du serveur (échec, non configuré). */
export interface ReponseSynchroTraccar {
  imei: string
  nom_traccar: string
  traccar: ResultatSynchroTraccar
  message: string | null
}

const RESULTATS_TRACCAR = new Set<string>(['cree', 'renomme', 'inchange', 'simule', 'non_configure', 'echec'])

/** Une phrase pour le bandeau de confirmation. */
export function phraseSynchroTraccar(r: Pick<ReponseSynchroTraccar, 'nom_traccar' | 'traccar' | 'message'>): string {
  switch (r.traccar) {
    case 'cree': return `Appareil créé dans Traccar sous le nom « ${r.nom_traccar} ».`
    case 'renomme': return `Appareil renommé dans Traccar : « ${r.nom_traccar} ».`
    case 'inchange': return `Traccar est déjà à jour (« ${r.nom_traccar} »).`
    case 'simule': return "Simulation : rien n'a été envoyé à Traccar."
    case 'non_configure': return 'Traccar non configuré : rien n’a été envoyé.'
    case 'echec': return `Échec de la synchronisation avec Traccar${r.message ? ` : ${r.message.replace(/[.\s]+$/, '')}` : ''}.`
  }
}

const ERREURS_SYNCHRO: Record<string, string> = {
  not_platform_admin: "Votre compte n'est pas administrateur de la plateforme : la synchronisation est refusée.",
  boitier_inconnu: "Ce boîtier n'est pas dans le registre. Rechargez la page.",
  imei_requis: "Indiquez l'identifiant du boîtier (IMEI).",
  imei_invalide: 'IMEI invalide : la synchronisation est refusée.',
  unauthorized: 'Session expirée — reconnectez-vous puis recommencez.',
  gps_non_configure: 'Traccar non configuré : rien n’a été envoyé.',
  db_error: 'Erreur de base de données : rien n’a été confirmé. Réessayez dans un instant.',
}

/** Traduit l'erreur de l'edge function (code de la réponse HTTP, sinon message brut) en phrase lisible. */
export function messageSynchroTraccar(code: string, detail = '', message = ''): string {
  const connue = ERREURS_SYNCHRO[code]
  if (connue) return connue
  const brut = message.toLowerCase()
  if (brut.includes('failed to fetch') || brut.includes('failed to send a request') || brut.includes('load failed') || brut.includes('networkerror')) {
    return 'Connexion impossible : vérifiez votre réseau puis réessayez. Rien n’a été envoyé à Traccar.'
  }
  const precis = detail || message
  return `La synchronisation avec Traccar a échoué${precis ? ` (${precis})` : ''}. Rien n’a été confirmé.`
}

/**
 * Crée ou renomme dans Traccar l'appareil d'un IMEI du registre. Un échec côté Traccar (injoignable, accès refusé) n'est PAS
 * une exception : la réponse porte `traccar: 'echec'` et sa phrase. Lève une `Error` au message en français pour le reste
 * (droits, boîtier inconnu, réseau).
 */
export async function synchroniserTraccar(invoke: AppelFonction, imei: string): Promise<ReponseSynchroTraccar> {
  const { data, error } = await invoke('gps-commande', { body: { action: 'synchroniser_plateforme', imei: imei.trim() } })
  if (error) {
    const e = await lireCorpsErreur(error)
    throw new Error(messageSynchroTraccar(e.code, e.detail, e.message))
  }
  const rep = (data && typeof data === 'object' ? data : null) as Record<string, unknown> | null
  if (!rep || rep['ok'] !== true) {
    throw new Error(messageSynchroTraccar(String(rep?.['error'] ?? ''), String(rep?.['detail'] ?? rep?.['message'] ?? '')))
  }
  const brut = String(rep['suivi'] ?? '')
  const traccar = (RESULTATS_TRACCAR.has(brut) ? brut : 'echec') as ResultatSynchroTraccar
  return {
    imei: String(rep['imei'] ?? imei.trim()),
    nom_traccar: String(rep['nom_suivi'] ?? ''),
    traccar,
    message: txt(rep['message']) ?? txt(rep['detail']),
  }
}
